import { QueryClient, QueryClientProvider, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  BadgeDollarSign,
  BarChart3,
  Brain,
  CalendarDays,
  Check,
  Clock3,
  EyeOff,
  Flame,
  Keyboard,
  LogOut,
  Moon,
  Plus,
  RefreshCw,
  ScrollText,
  ShieldAlert,
  Sparkles,
  Sun,
  Swords,
  Trophy,
  UserRound,
  Wand2,
  X
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import type { ActiveQuestionPayload, AnswerResultPayload, ApiUser, DashboardQuestion, LeaderboardRow } from "../shared/api";
import { CORE_CATEGORIES } from "../shared/categories";
import { TRIVIA_OFF_QUESTION_COUNT, WEEKLY_QUESTION_COUNT } from "../shared/game";
import { questionTimerMs } from "../shared/time";
import "./styles.css";

type DashboardData = {
  week: { id: string; end_at: string; status: string } | null;
  days: Array<{ dayOfWeek: number; label: string; questions: DashboardQuestion[] }>;
  leaderboard: LeaderboardRow[];
  triviaOffs: TriviaOffSummary[];
  antiCheatCount: number;
};

type TriviaOffSummary = {
  id: string;
  name: string;
  challengerUserId: string;
  status: string;
  participantStatus: string;
  categories: string[];
  questionCount: number;
  stakeAmount: number;
  deadlineAt: string;
  winnerUserId: string | null;
  participantCount: number;
  myAnsweredCount: number;
  totalSubmittedCount: number;
  totalRequiredCount: number;
};

type TriviaOffResults = {
  triviaOff: { id: string; name: string; status: string; winnerUserId: string | null; stakeAmount: number; tieUserIds: string[] };
  participants: Array<{
    user_id: string;
    name: string;
    status: string;
    score: number;
    response_ms_total: number;
    suspicious_event_count: number;
    completed_at: string | null;
  }>;
  questions: Array<{
    id: string;
    displayOrder: number;
    category: string;
    prompt: string;
    correctChoice: string | null;
    correctTextAnswer: string | null;
    answerType: "multipleChoice" | "textInput";
    answers: Array<{
      name: string;
      selectedChoice: string | null;
      displayAnswer: string | null;
      text_answer: string | null;
      needs_manual_review: number | null;
      is_correct: number | null;
      voided_for_cheat: number | null;
      suspicious_event_count: number | null;
      submitted_at: string | null;
    }>;
  }>;
};

type StatsData = {
  playerStats: Array<{
    user_id: string;
    name: string;
    avatar_color: string;
    started: number;
    submitted: number;
    correct: number;
    misses: number;
    flags: number;
    avg_correct_ms: number | null;
  }>;
  categoryStats: Array<{ category: string; attempts: number; correct: number; avg_correct_ms: number | null; flags: number }>;
  dailyTrend: Array<{ day_of_week: number; submitted: number; correct: number; avg_correct_ms: number | null; flags: number }>;
  fastest: Array<{ name: string; category: string; response_ms: number }>;
};

type CategoryCatalogData = {
  totalActive: number;
  blind: boolean;
  categories: Array<{
    category: string;
    count: number;
    hard_count: number;
    multiple_choice_count: number;
    text_input_count: number;
    seed_count: number;
    ai_count: number;
    current_week_count: number;
  }>;
};

type AdminDispute = {
  id: string;
  playerName: string;
  playerEmail: string;
  reason: string;
  status: string;
  adminNote: string | null;
  createdAt: string;
  resolvedAt: string | null;
  context: "weekly" | "trivia-off" | "bank";
  question: {
    id: string;
    prompt: string;
    category: string;
    difficulty: string;
    answerType: "multipleChoice" | "textInput";
    correctChoice: string | null;
    correctTextAnswer: string | null;
    explanation: string;
  };
  answer: {
    id: string;
    selectedChoice: string | null;
    textAnswer: string | null;
    normalizedTextAnswer: string | null;
    gradingConfidence: number | null;
    isCorrect: boolean;
    needsManualReview: boolean;
    voidedForCheat: boolean;
    voidedByAdmin: boolean;
    suspiciousEventCount: number;
    submittedAt: string | null;
    scheduledQuestionId: string | null;
    triviaOffQuestionId: string | null;
  } | null;
  placement: {
    dayOfWeek: number | null;
    displayOrder: number | null;
    triviaOffName: string | null;
  };
};

type HistoryData = {
  weeks: Array<{
    id: string;
    start_at: string;
    end_at: string;
    status: string;
    reveal: boolean;
    questions: Array<{
      id: string;
      dayOfWeek: number;
      displayOrder: number;
      category: string;
      difficulty: string;
      prompt: string | null;
      correctChoice: string | null;
      correctTextAnswer: string | null;
      answerType: "multipleChoice" | "textInput";
      answers: Array<{
        name: string;
        selectedChoice: string | null;
        displayAnswer: string | null;
        selected_choice_index: number | null;
        text_answer: string | null;
        normalized_text_answer: string | null;
        grading_confidence: number | null;
        needs_manual_review: number | null;
        is_correct: number | null;
        voided_for_cheat: number | null;
        voided_by_admin: number | null;
        suspicious_event_count: number | null;
        submitted_at: string | null;
      }>;
    }>;
  }>;
};

type RecentGeneratedQuestion = {
  id: string;
  prompt: string;
  category: string;
  difficulty: "easy" | "medium" | "hard";
  answerType: "multipleChoice" | "textInput";
  source: string;
  active: boolean;
  createdAt: string;
};

type PromptOnlyQuestion = RecentGeneratedQuestion;

type AuthResponse = {
  user: ApiUser | null;
  devLoginUrl?: string;
};

const queryClient = new QueryClient();

function formatBucks(value: number | null | undefined): string {
  const amount = Number(value ?? 0);
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(1).replace(/\.0$/, "");
}

async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    headers: options?.body ? { "Content-Type": "application/json", ...(options.headers ?? {}) } : options?.headers,
    ...options
  });
  const payload = (await response.json().catch(() => ({}))) as T & {
    error?: string;
    details?: Array<{ path?: Array<string | number>; message?: string }>;
  };
  if (!response.ok) {
    const details = payload.details?.map((issue) => `${issue.path?.join(".") || "request"}: ${issue.message ?? "invalid"}`).join("; ");
    throw new Error(details || payload.error || "Request failed");
  }
  return payload;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Shell />
    </QueryClientProvider>
  );
}

function Shell() {
  const query = useQuery({ queryKey: ["me"], queryFn: () => api<AuthResponse>("/api/me") });
  const token = new URLSearchParams(window.location.search).get("token");

  if (token) return <MagicVerifier token={token} />;
  if (query.isLoading) return <LoadingScreen />;
  if (!query.data?.user) return <Login />;
  if (query.data.user.needsPasswordSetup) return <PasswordSetup user={query.data.user} />;
  return <Dashboard user={query.data.user} />;
}

function LoadingScreen() {
  return (
    <main className="center-screen">
      <div className="brand-orb">
        <Brain size={34} />
      </div>
    </main>
  );
}

function Login() {
  const [email, setEmail] = useState("keenankdolan@gmail.com");
  const [password, setPassword] = useState("");
  const [devLink, setDevLink] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const magic = useMutation({
    mutationFn: () => api<{ sent: boolean; devLoginUrl?: string }>("/api/auth/request-link", { method: "POST", body: JSON.stringify({ email }) }),
    onSuccess: (data) => setDevLink(data.devLoginUrl ?? null)
  });
  const login = useMutation({
    mutationFn: () => api<AuthResponse>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] })
  });

  return (
    <main className="auth-page">
      <section className="auth-panel">
        <div className="logo-lockup">
          <span className="logo-mark">
            <Brain size={26} />
          </span>
          <div>
            <p className="eyebrow">Private trivia league</p>
            <h1>Bomber Brain</h1>
          </div>
        </div>
        <div className="auth-copy">
          <p>Seven players. Ten daily questions. Bomber Bucks on the line.</p>
        </div>
        <label>
          Email
          <input value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" />
        </label>
        <label>
          Password
          <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="current-password" />
        </label>
        <div className="button-row">
          <button className="primary" onClick={() => login.mutate()} disabled={login.isPending || !password}>
            Login
          </button>
          <button onClick={() => magic.mutate()} disabled={magic.isPending}>
            Send magic link
          </button>
        </div>
        {devLink ? (
          <a className="dev-link" href={devLink}>
            Open local magic link
          </a>
        ) : null}
        <MutationError mutation={login} />
        <MutationError mutation={magic} />
      </section>
    </main>
  );
}

function MagicVerifier({ token }: { token: string }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api<AuthResponse>("/api/auth/verify", { method: "POST", body: JSON.stringify({ token }) }),
    onSuccess: () => {
      window.history.replaceState({}, "", "/");
      queryClient.invalidateQueries({ queryKey: ["me"] });
    }
  });

  useEffect(() => {
    mutation.mutate();
  }, []);

  return (
    <main className="center-screen">
      <section className="mini-panel">
        <Clock3 />
        <h1>Checking your link</h1>
        <MutationError mutation={mutation} />
      </section>
    </main>
  );
}

function PasswordSetup({ user }: { user: ApiUser }) {
  const [password, setPassword] = useState("");
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api<AuthResponse>("/api/auth/password", { method: "POST", body: JSON.stringify({ password }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] })
  });
  return (
    <main className="auth-page">
      <section className="auth-panel">
        <p className="eyebrow">Welcome, {user.name}</p>
        <h1>Create your password</h1>
        <label>
          Password
          <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="new-password" />
        </label>
        <button className="primary" onClick={() => mutation.mutate()} disabled={password.length < 10 || mutation.isPending}>
          Save password
        </button>
        <MutationError mutation={mutation} />
      </section>
    </main>
  );
}

function Dashboard({ user }: { user: ApiUser }) {
  const [activeQuestion, setActiveQuestion] = useState<ActiveQuestionPayload | null>(null);
  const [view, setView] = useState<"weekly" | "leaderboard" | "trivia" | "categories" | "stats" | "history" | "admin">("weekly");
  const [theme, setTheme] = useState<"light" | "dark">(() => (localStorage.getItem("bomber-brain-theme") === "dark" ? "dark" : "light"));
  const queryClient = useQueryClient();
  const dashboard = useQuery({ queryKey: ["dashboard"], queryFn: () => api<DashboardData>("/api/dashboard"), refetchInterval: 15_000 });
  const logout = useMutation({
    mutationFn: () => api("/api/auth/logout", { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] })
  });

  if (dashboard.isLoading) return <LoadingScreen />;
  const data = dashboard.data;
  if (!data) return <LoadingScreen />;
  const me = data.leaderboard.find((row) => row.userId === user.id);

  return (
    <main className={`app-shell theme-${theme}`}>
      <header className="topbar">
        <div className="logo-lockup compact">
          <span className="logo-mark">
            <Brain size={22} />
          </span>
          <div>
            <p className="eyebrow">Bomber Brain</p>
            <h1>Weekly room</h1>
          </div>
        </div>
        <div className="top-actions">
          <Pill className="welcome-pill" icon={<UserRound size={16} />} label={`Welcome back, ${user.name}`} />
          <Pill icon={<BadgeDollarSign size={16} />} label={`${formatBucks(me?.bomberBucks ?? user.bomberBucks)} BB`} />
          <button
            className="icon-button"
            onClick={() =>
              setTheme((current) => {
                const next = current === "dark" ? "light" : "dark";
                localStorage.setItem("bomber-brain-theme", next);
                return next;
              })
            }
            title={theme === "dark" ? "Use light mode" : "Use dark mode"}
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button className="icon-button" onClick={() => logout.mutate()} title="Log out">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <section className="hero-band">
        <div>
          <p className="eyebrow">Week locks Sunday at midnight PT</p>
          <h2>Bomber Brain</h2>
          <p className="hero-copy">
            Ten daily questions unlock Monday through Sunday. Choice questions run 10 seconds; typed questions run 15 seconds. Answer once, no
            take-backs.
          </p>
          <div className="rules-grid">
            <span>{WEEKLY_QUESTION_COUNT}-question weekly board</span>
            <span>+1 BB for each correct answer</span>
            <span>Trivia-Offs escrow real leaderboard points</span>
            <span>Leaving the app voids the answer</span>
          </div>
        </div>
        <div className="hero-stats">
          <Stat label="Your bucks" value={formatBucks(me?.bomberBucks ?? user.bomberBucks)} icon={<BadgeDollarSign />} />
          <Stat label="Weekly correct" value={String(me?.weeklyCorrect ?? 0)} icon={<Check />} />
          <Stat label="Flags" value={String(data.antiCheatCount)} icon={<ShieldAlert />} />
        </div>
      </section>

      <nav className="tabs">
        <button className={view === "weekly" ? "active" : ""} onClick={() => setView("weekly")}>
          Weekly
        </button>
        <button className={view === "leaderboard" ? "active" : ""} onClick={() => setView("leaderboard")}>
          Leaderboard
        </button>
        <button className={view === "trivia" ? "active" : ""} onClick={() => setView("trivia")}>
          Trivia-Offs
        </button>
        <button className={view === "categories" ? "active" : ""} onClick={() => setView("categories")}>
          Categories
        </button>
        <button className={view === "stats" ? "active" : ""} onClick={() => setView("stats")}>
          Stats
        </button>
        <button className={view === "history" ? "active" : ""} onClick={() => setView("history")}>
          History
        </button>
        {user.role === "admin" ? (
          <button className={view === "admin" ? "active" : ""} onClick={() => setView("admin")}>
            Admin
          </button>
        ) : null}
      </nav>

      <AnimatePresence mode="wait">
        {view === "weekly" ? (
          <motion.section key="weekly" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <WeeklyBoard data={data} onStart={setActiveQuestion} />
          </motion.section>
        ) : null}
        {view === "trivia" ? (
          <motion.section key="trivia" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <TriviaOffs user={user} data={data} onStart={setActiveQuestion} />
          </motion.section>
        ) : null}
        {view === "leaderboard" ? (
          <motion.section key="leaderboard" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Leaderboard rows={data.leaderboard} expanded />
          </motion.section>
        ) : null}
        {view === "categories" ? (
          <motion.section key="categories" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <CategoriesPanel />
          </motion.section>
        ) : null}
        {view === "stats" ? (
          <motion.section key="stats" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <StatsPanel />
          </motion.section>
        ) : null}
        {view === "history" ? (
          <motion.section key="history" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <HistoryPanel />
          </motion.section>
        ) : null}
        {view === "admin" && user.role === "admin" ? (
          <motion.section key="admin" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <AdminPanel />
          </motion.section>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {activeQuestion ? (
          <QuestionModal
            question={activeQuestion}
            onClose={() => {
              const triviaOffId = activeQuestion.triviaOffId;
              setActiveQuestion(null);
              queryClient.invalidateQueries({ queryKey: ["dashboard"] });
              if (triviaOffId) {
                queryClient.invalidateQueries({ queryKey: ["trivia-off-questions", triviaOffId] });
                queryClient.invalidateQueries({ queryKey: ["trivia-off-results", triviaOffId] });
              }
            }}
          />
        ) : null}
      </AnimatePresence>
    </main>
  );
}

function WeeklyBoard({ data, onStart }: { data: DashboardData; onStart: (question: ActiveQuestionPayload) => void }) {
  const questionCount = data.days.reduce((total, day) => total + day.questions.length, 0);
  const start = useMutation({
    mutationFn: (id: string) => api<ActiveQuestionPayload | AnswerResultPayload>(`/api/questions/${id}/start`, { method: "POST" }),
    onSuccess: (payload) => {
      if ("prompt" in payload) onStart(payload);
    }
  });

  return (
    <div className="content-grid">
      <section className="panel span-3">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Weekly set</p>
            <h2>{questionCount} questions</h2>
          </div>
          <Pill icon={<Clock3 size={16} />} label="10s choice / 15s typed" />
        </div>
        <div className="day-grid">
          {data.days.map((day) => (
            <div className="day-lane" key={day.dayOfWeek}>
              <h3>{day.label}</h3>
              {day.questions.map((question) => (
                <button
                  className={`question-row ${question.status}`}
                  key={question.id}
                  disabled={question.status === "locked" || question.status === "answered" || start.isPending}
                  onClick={() => start.mutate(question.id)}
                >
                  <span>
                    <strong>{question.category}</strong>
                    <small>
                      {question.difficulty} / {question.answerType === "textInput" ? "typed" : "choice"}
                    </small>
                  </span>
                  <span className="status-dot">{question.status === "answered" ? (question.isCorrect ? "1" : "0") : question.displayOrder}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function QuestionModal({ question, onClose }: { question: ActiveQuestionPayload; onClose: () => void }) {
  const [remaining, setRemaining] = useState(question.timerMs ?? questionTimerMs(question.answerType));
  const [hidden, setHidden] = useState(false);
  const [result, setResult] = useState<AnswerResultPayload | null>(null);
  const [textAnswer, setTextAnswer] = useState("");
  const [disputeSent, setDisputeSent] = useState(false);
  const queryClient = useQueryClient();

  const submit = useMutation({
    mutationFn: (answer: { selectedChoiceIndex?: number; textAnswer?: string }) => {
      const endpoint =
        question.context === "weekly" ? `/api/questions/${question.id}/answer` : `/api/trivia-off-questions/${question.id}/answer`;
      return api<AnswerResultPayload>(endpoint, { method: "POST", body: JSON.stringify(answer) });
    },
    onSuccess: (payload) => {
      setResult(payload);
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      const triviaOffId = payload.triviaOffId ?? question.triviaOffId;
      if (triviaOffId) {
        queryClient.invalidateQueries({ queryKey: ["trivia-off-questions", triviaOffId] });
        queryClient.invalidateQueries({ queryKey: ["trivia-off-results", triviaOffId] });
        queryClient.refetchQueries({ queryKey: ["trivia-off-questions", triviaOffId], type: "active" });
      }
    }
  });
  const antiCheat = useMutation({
    mutationFn: (eventType: string) =>
      api("/api/anti-cheat", {
        method: "POST",
        body: JSON.stringify({
          eventType,
          scheduledQuestionId: question.context === "weekly" ? question.id : undefined,
          triviaOffQuestionId: question.context === "trivia-off" ? question.id : undefined,
          answerId: question.answerId,
          remainingMs: remaining,
          metadata: { userAgent: navigator.userAgent }
        })
      })
  });

  useEffect(() => {
    const interval = window.setInterval(() => {
      setRemaining(Math.max(0, new Date(question.deadlineAt).getTime() - Date.now()));
    }, 100);
    return () => window.clearInterval(interval);
  }, [question.deadlineAt]);

  useEffect(() => {
    if (result) return;
    const hide = (eventType: string) => {
      setHidden(true);
      antiCheat.mutate(eventType);
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") hide("visibility-hidden");
    };
    const onBlur = () => hide("blur");
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
    };
  }, [result, remaining]);

  useEffect(() => {
    if (remaining === 0 && !result && !submit.isPending) {
      submit.mutate({ selectedChoiceIndex: -1 });
    }
  }, [remaining, result, submit.isPending]);

  const disputeMutation = useMutation({
    mutationFn: () =>
      api(`/api/questions/${question.questionId}/disputes`, {
        method: "POST",
        body: JSON.stringify({ reason: "Player marked this question for review.", answerId: result?.answerId })
      }),
    onSuccess: () => setDisputeSent(true)
  });

  return (
    <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.section className="question-modal" initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}>
        <div className="modal-top">
          <Pill icon={<Flame size={16} />} label={question.category} />
          <div
            className="timer"
            style={{ "--progress": `${remaining / (question.timerMs ?? questionTimerMs(question.answerType))}` } as React.CSSProperties}
          >
            {(remaining / 1000).toFixed(1)}
          </div>
          <button className="icon-button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        {hidden && !result ? (
          <div className="warning">
            <AlertTriangle />
            <h2>Question hidden</h2>
            <p>The app lost focus. This answer has been voided.</p>
          </div>
        ) : null}
        {!hidden && !result ? (
          <>
            <h2 className="question-text">{question.prompt}</h2>
            {question.answerType === "multipleChoice" ? (
              <div className="answer-grid">
                {(question.choices ?? []).map((choice, index) => (
                  <button key={choice} disabled={submit.isPending || remaining === 0} onClick={() => submit.mutate({ selectedChoiceIndex: index })}>
                    {choice}
                  </button>
                ))}
              </div>
            ) : (
              <form
                className="text-answer-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  submit.mutate({ textAnswer });
                }}
              >
                <div className="text-answer-shell">
                  <Keyboard size={22} />
                  <input
                    autoFocus
                    value={textAnswer}
                    onChange={(event) => setTextAnswer(event.target.value)}
                    placeholder="Type the answer"
                    maxLength={200}
                    disabled={submit.isPending || remaining === 0}
                  />
                </div>
                <button className="primary" disabled={submit.isPending || remaining === 0 || !textAnswer.trim()}>
                  Submit typed answer
                </button>
                <p className="fine-print">Close answers count: last names and minor typos are accepted when they are unambiguous.</p>
              </form>
            )}
          </>
        ) : null}
        {result ? (
          <div className="result-block">
            <div className={result.isCorrect ? "result-good" : result.needsManualReview ? "result-review" : "result-bad"}>
              {result.isCorrect ? "Correct" : result.needsManualReview ? "Needs review" : "Miss"}
            </div>
            <p>{result.explanation}</p>
            {result.answerType === "textInput" ? (
              <div className="answer-reveal">
                <span>
                  <b>Your answer</b>
                  {result.textAnswer || "No answer"}
                </span>
                <span>
                  <b>Accepted answer</b>
                  {result.correctTextAnswer ?? "Hidden"}
                </span>
                {result.matchedTextAnswer ? (
                  <span>
                    <b>Matched</b>
                    {result.matchedTextAnswer}
                  </span>
                ) : null}
              </div>
            ) : (
              <div className="answer-reveal">
                <span>
                  <b>Correct answer</b>
                  {result.correctChoice}
                </span>
              </div>
            )}
            {result.voidedForCheat ? <p className="fine-print">Voided because the app lost focus during the question.</p> : null}
            {result.needsManualReview ? <p className="fine-print">This typed answer was close enough to queue for admin review, but it is not counted until approved.</p> : null}
            {result.triviaOffCompleted ? (
              <div className="info-callout">
                <b>{result.triviaOffTieUserIds?.length ? "Trivia-Off complete: tie" : "Trivia-Off complete"}</b>
                <span>{result.triviaOffTieUserIds?.length ? "The pot splits between the tied players." : "Open Trivia-Off results to see the winner and full answer sheet."}</span>
              </div>
            ) : null}
            <div className="result-actions">
              <button onClick={() => disputeMutation.mutate()} disabled={disputeMutation.isPending || disputeSent}>
                {disputeSent ? "Flagged" : "Flag for admin"}
              </button>
            </div>
          </div>
        ) : null}
      </motion.section>
    </motion.div>
  );
}

function TriviaOffs({ user, data, onStart }: { user: ApiUser; data: DashboardData; onStart: (question: ActiveQuestionPayload) => void }) {
  const [participantUserId, setParticipantUserId] = useState("");
  const [category, setCategory] = useState("Seattle Mariners");
  const [stakeAmount, setStakeAmount] = useState(5);
  const [selectedTriviaOffId, setSelectedTriviaOffId] = useState<string | null>(null);
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const [activeTriviaOffQuestionId, setActiveTriviaOffQuestionId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: () =>
      api("/api/trivia-offs", {
        method: "POST",
        body: JSON.stringify({
          name: `${category} Trivia-Off`,
          participantUserIds: [participantUserId],
          categories: [category],
          questionCount: TRIVIA_OFF_QUESTION_COUNT,
          stakeAmount,
          deadlineAt: data.week?.end_at ?? new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString()
        })
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["dashboard"] })
  });
  const respond = useMutation({
    mutationFn: ({ id, response }: { id: string; response: "accept" | "decline" }) =>
      api(`/api/trivia-offs/${id}/respond`, { method: "POST", body: JSON.stringify({ response }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["trivia-off-questions"] });
    }
  });
  const questions = useQuery({
    queryKey: ["trivia-off-questions", selectedTriviaOffId],
    enabled: Boolean(selectedTriviaOffId),
    queryFn: () =>
      api<{ questions: Array<{ id: string; display_order: number; category: string; difficulty: string; answer_type: "multipleChoice" | "textInput"; submitted_at: string | null }> }>(
        `/api/trivia-offs/${selectedTriviaOffId}/questions`
      ),
    refetchInterval: selectedTriviaOffId ? 2500 : false
  });
  const results = useQuery({
    queryKey: ["trivia-off-results", selectedResultId],
    enabled: Boolean(selectedResultId),
    queryFn: () => api<TriviaOffResults>(`/api/trivia-offs/${selectedResultId}/results`)
  });
  const start = useMutation({
    mutationFn: (id: string) => api<ActiveQuestionPayload | AnswerResultPayload>(`/api/trivia-off-questions/${id}/start`, { method: "POST" }),
    onMutate: (id) => setActiveTriviaOffQuestionId(id),
    onSuccess: (payload) => {
      if ("prompt" in payload) onStart(payload);
      const triviaOffId = payload.triviaOffId ?? selectedTriviaOffId;
      if (triviaOffId) {
        queryClient.invalidateQueries({ queryKey: ["trivia-off-questions", triviaOffId] });
        queryClient.invalidateQueries({ queryKey: ["trivia-off-results", triviaOffId] });
      }
    },
    onSettled: () => {
      setActiveTriviaOffQuestionId(null);
    }
  });
  const opponents = data.leaderboard.filter((row) => row.userId !== user.id);

  return (
    <div className="content-grid">
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Side pot</p>
            <h2>Start a Trivia-Off</h2>
          </div>
          <Swords />
        </div>
        <div className="info-callout">
          <b>{TRIVIA_OFF_QUESTION_COUNT} questions, same deadline</b>
          <span>
            Pick one opponent and one category. You escrow the stake from your Bomber Bucks. If they accept, both players answer by Sunday midnight PT.
            Winner takes the escrowed pot, and results unlock to the league when the challenge completes.
          </span>
        </div>
        <label>
          Opponent
          <select value={participantUserId} onChange={(event) => setParticipantUserId(event.target.value)}>
            <option value="">Choose player</option>
            {opponents.map((opponent) => (
              <option key={opponent.userId} value={opponent.userId}>
                {opponent.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Category
          <select value={category} onChange={(event) => setCategory(event.target.value)}>
            {CORE_CATEGORIES.map((cat) => (
              <option key={cat}>{cat}</option>
            ))}
          </select>
        </label>
        <label>
          Bomber Bucks stake
          <input type="number" min={1} value={stakeAmount} onChange={(event) => setStakeAmount(Number(event.target.value))} />
        </label>
        <button className="primary" disabled={!participantUserId || create.isPending} onClick={() => create.mutate()}>
          <Plus size={16} />
          Create {TRIVIA_OFF_QUESTION_COUNT}-question challenge
        </button>
        <MutationError mutation={create} />
      </section>
      <section className="panel span-2">
        <div>
          <p className="eyebrow">Challenges</p>
          <h2>Trivia-Off board</h2>
        </div>
        <div className="challenge-list">
          {data.triviaOffs.map((challenge) => (
            <div className="challenge-card" key={challenge.id}>
              <div>
                <h3>{challenge.name}</h3>
                <p>
                  {challenge.questionCount} questions / {challenge.categories.join(", ")} / {formatBucks(challenge.stakeAmount)} BB pot / {challenge.status}
                </p>
                <div className="challenge-progress">
                  <span>You: {challenge.myAnsweredCount}/{challenge.questionCount}</span>
                  <span>
                    Total: {challenge.totalSubmittedCount}/{challenge.totalRequiredCount}
                  </span>
                </div>
                <small>{triviaOffCopy(challenge)}</small>
              </div>
              <div className="button-row">
                {challenge.participantStatus === "invited" ? (
                  <>
                    <button onClick={() => respond.mutate({ id: challenge.id, response: "accept" })}>Accept</button>
                    <button onClick={() => respond.mutate({ id: challenge.id, response: "decline" })}>Decline</button>
                  </>
                ) : null}
                {challenge.status === "active" && challenge.participantStatus !== "viewer" ? (
                  <button onClick={() => setSelectedTriviaOffId(challenge.id)}>Play</button>
                ) : null}
                {challenge.status === "completed" ? <button onClick={() => setSelectedResultId(challenge.id)}>Results</button> : null}
                <button
                  className="icon-button"
                  aria-label="Refresh Trivia-Offs"
                  onClick={() => {
                    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
                    queryClient.invalidateQueries({ queryKey: ["trivia-off-questions", challenge.id] });
                    queryClient.invalidateQueries({ queryKey: ["trivia-off-results", challenge.id] });
                  }}
                >
                  <RefreshCw size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
        {selectedTriviaOffId ? (
          <div className="trivia-question-list">
            <h3>Challenge questions</h3>
            {questions.data?.questions.map((question) => (
              <button
                key={question.id}
                className={`trivia-question-button ${question.submitted_at ? "answered" : activeTriviaOffQuestionId === question.id ? "started" : "available"}`}
                disabled={Boolean(question.submitted_at)}
                onClick={() => start.mutate(question.id)}
              >
                <b>#{question.display_order}</b>
                <span>
                  {question.category} / {question.answer_type === "textInput" ? "typed" : "choice"}
                </span>
                <em>{question.submitted_at ? "done" : activeTriviaOffQuestionId === question.id ? "in progress" : "ready"}</em>
              </button>
            ))}
          </div>
        ) : null}
        {selectedResultId ? (
          <div className="trivia-results">
            <h3>{results.data?.triviaOff.name ?? "Trivia-Off results"}</h3>
            {results.data ? (
              <div className="trivia-result-summary">
                <div>
                  <p className="eyebrow">{results.data.triviaOff.tieUserIds.length ? "Result" : "Winner"}</p>
                  <strong>
                    {results.data.triviaOff.tieUserIds.length
                      ? "Tie"
                      : results.data.participants.find((participant) => participant.user_id === results.data?.triviaOff.winnerUserId)?.name ?? "Tie"}
                  </strong>
                  <span>
                    {results.data.triviaOff.tieUserIds.length
                      ? `Split the ${formatBucks(results.data.triviaOff.stakeAmount)} BB pot (${formatBucks(
                          results.data.triviaOff.stakeAmount / results.data.triviaOff.tieUserIds.length
                        )} BB each)`
                      : `Won the ${formatBucks(results.data.triviaOff.stakeAmount)} BB pot`}
                  </span>
                </div>
                {results.data.participants.map((participant) => (
                  <div key={participant.user_id}>
                    <p className="eyebrow">{participant.name}</p>
                    <strong>{participant.score}/10</strong>
                    <span>
                      {participant.response_ms_total ? `${(participant.response_ms_total / 1000).toFixed(1)}s correct time` : "No correct time"} /{" "}
                      {participant.suspicious_event_count} flags
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
            {results.data?.questions.map((question) => (
              <div className="result-card" key={question.id}>
                <p className="eyebrow">
                  #{question.displayOrder} {question.category}
                </p>
                <h4>{question.prompt}</h4>
                <p>
                  <b>Correct:</b> {question.correctTextAnswer ?? question.correctChoice}
                </p>
                {question.answers.map((answer) => (
                  <p key={answer.name}>
                    {answer.name}: {answer.displayAnswer ?? answer.selectedChoice ?? "No answer"} {answer.is_correct ? "Correct" : answer.needs_manual_review ? "Review" : "Miss"}
                    {answer.suspicious_event_count ? ` / ${answer.suspicious_event_count} flags` : ""}
                  </p>
                ))}
              </div>
            ))}
            {results.error ? <p className="error">{results.error.message}</p> : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function triviaOffCopy(challenge: TriviaOffSummary): string {
  if (challenge.participantStatus === "invited") return `You were challenged. Accept to play for the ${formatBucks(challenge.stakeAmount)} BB winner-take-pot.`;
  if (challenge.status === "pending") return "Waiting on the invited player to accept or decline.";
  if (challenge.status === "active") return `Accepted. Both players have until Sunday midnight PT to finish. Winner takes the ${formatBucks(challenge.stakeAmount)} BB pot.`;
  if (challenge.status === "completed" && !challenge.winnerUserId) return `Complete. It ended in a tie, so the ${formatBucks(challenge.stakeAmount)} BB pot was split.`;
  if (challenge.status === "completed") return `Complete. The winner took the ${formatBucks(challenge.stakeAmount)} BB pot, and results are visible to the full league.`;
  if (challenge.status === "declined") return "Declined. The stake was returned.";
  return "Challenge closed.";
}

function CategoriesPanel() {
  const [filter, setFilter] = useState("");
  const categories = useQuery({ queryKey: ["categories"], queryFn: () => api<CategoryCatalogData>("/api/categories"), refetchInterval: 60_000 });
  const rows = categories.data?.categories ?? [];
  const visibleRows = rows.filter((row) => row.category.toLowerCase().includes(filter.trim().toLowerCase()));
  const currentWeekCount = rows.reduce((total, row) => total + Number(row.current_week_count ?? 0), 0);

  return (
    <div className="content-grid">
      <section className="panel span-3">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Question bank</p>
            <h2>Categories</h2>
            <p className="fine-print">
              Canonical category coverage syncs with admin. Prompts, choices, and answers stay hidden until they are played or archived.
            </p>
          </div>
          <ScrollText />
        </div>
        <div className="stat-grid">
          <Stat label="Active questions" value={String(categories.data?.totalActive ?? 0)} icon={<ScrollText />} />
          <Stat label="Categories" value={String(rows.length)} icon={<Brain />} />
          <Stat label="On this board" value={String(currentWeekCount)} icon={<CalendarDays />} />
        </div>
      </section>
      <section className="panel span-3">
        <label>
          Find a category
          <input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Search movies, Mariners, geography..." />
        </label>
        {categories.isLoading ? <p className="fine-print">Loading categories...</p> : null}
        {categories.error ? <p className="error">{categories.error.message}</p> : null}
        <div className="category-directory">
          {visibleRows.map((row) => {
            const hardRatio = row.count ? Math.round((row.hard_count / row.count) * 100) : 0;
            const typedRatio = row.count ? Math.round((row.text_input_count / row.count) * 100) : 0;
            return (
              <article className="category-card" key={row.category}>
                <div className="category-card-top">
                  <div>
                    <p className="eyebrow">{row.current_week_count ? `${row.current_week_count} on current board` : "Banked category"}</p>
                    <h3>{row.category}</h3>
                  </div>
                  <b>{row.count}</b>
                </div>
                <div className="category-metrics">
                  <span>
                    Choice <b>{row.multiple_choice_count}</b>
                  </span>
                  <span>
                    Typed <b>{row.text_input_count}</b>
                  </span>
                  <span>
                    Hard <b>{row.hard_count}</b>
                  </span>
                  <span>
                    AI <b>{row.ai_count}</b>
                  </span>
                </div>
                <div className="category-meter" aria-label={`${row.category} hard question mix`}>
                  <i style={{ width: `${hardRatio}%` }} />
                </div>
                <small>
                  {typedRatio}% typed / {row.seed_count} seed / {row.ai_count} generated
                </small>
              </article>
            );
          })}
        </div>
        {!categories.isLoading && !visibleRows.length ? <p className="fine-print">No categories match that search.</p> : null}
      </section>
    </div>
  );
}

function StatsPanel() {
  const stats = useQuery({ queryKey: ["stats"], queryFn: () => api<StatsData>("/api/stats"), refetchInterval: 20_000 });
  const topPlayer = stats.data?.playerStats[0];
  return (
    <div className="content-grid">
      <section className="panel span-3">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Next gen stats</p>
            <h2>League analytics</h2>
          </div>
          <BarChart3 />
        </div>
        <div className="stat-grid stats-kpis">
          <Stat label="Top pace" value={topPlayer?.name ?? "-"} icon={<Trophy />} />
          <Stat label="Fastest avg" value={topPlayer?.avg_correct_ms ? `${(topPlayer.avg_correct_ms / 1000).toFixed(1)}s` : "-"} icon={<Clock3 />} />
          <Stat label="Categories tracked" value={String(stats.data?.categoryStats.length ?? 0)} icon={<ScrollText />} />
        </div>
      </section>
      <section className="panel span-2">
        <p className="eyebrow">Player form</p>
        <div className="stats-table">
          {stats.data?.playerStats.map((row) => (
            <div className="stats-row" key={row.user_id}>
              <span className="avatar" style={{ background: row.avatar_color }}>
                {row.name.slice(0, 1)}
              </span>
              <div className="stats-player">
                <b>{row.name}</b>
                <span>{row.submitted} done</span>
              </div>
              <div className="stats-row-metrics">
                <span>{row.correct} correct</span>
                <span>{row.submitted ? `${Math.round((row.correct / row.submitted) * 100)}%` : "0%"}</span>
                <span>{row.avg_correct_ms ? `${(row.avg_correct_ms / 1000).toFixed(1)}s` : "no avg"}</span>
                <span>{row.flags} flags</span>
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="panel">
        <p className="eyebrow">Fastest correct</p>
        <div className="mini-list">
          {stats.data?.fastest.map((row, index) => (
            <p key={`${row.name}-${row.category}-${index}`}>
              <b>{row.name}</b> {row.category} {(row.response_ms / 1000).toFixed(1)}s
            </p>
          ))}
        </div>
      </section>
      <section className="panel span-2">
        <p className="eyebrow">Category strengths</p>
        <div className="coverage-list">
          {stats.data?.categoryStats.map((row) => {
            const rate = row.attempts ? Math.round((row.correct / row.attempts) * 100) : 0;
            return (
              <span key={row.category}>
                {row.category}
                <b>{rate}%</b>
              </span>
            );
          })}
        </div>
      </section>
      <section className="panel">
        <p className="eyebrow">Daily trend</p>
        <div className="trend-bars">
          {stats.data?.dailyTrend.map((row) => {
            const pct = row.submitted ? Math.round((row.correct / row.submitted) * 100) : 0;
            return (
              <span key={row.day_of_week} style={{ "--height": `${Math.max(8, pct)}%` } as React.CSSProperties}>
                <i />
                <b>{pct}%</b>
                <small>D{row.day_of_week}</small>
              </span>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function HistoryPanel() {
  const history = useQuery({ queryKey: ["history"], queryFn: () => api<HistoryData>("/api/history") });
  const [selectedWeekId, setSelectedWeekId] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState(1);
  const weeks = history.data?.weeks ?? [];
  const selectedWeek = weeks.find((week) => week.id === selectedWeekId) ?? weeks[0];

  useEffect(() => {
    if (!selectedWeekId && weeks[0]) setSelectedWeekId(weeks[0].id);
  }, [selectedWeekId, weeks]);

  const selectedQuestions = selectedWeek?.questions.filter((question) => question.dayOfWeek === selectedDay) ?? [];
  const selectedTotals = selectedQuestions.reduce(
    (totals, question) => {
      const answered = question.answers.filter((answer) => answer.submitted_at).length;
      const correct = question.answers.filter((answer) => answer.is_correct).length;
      const flags = question.answers.reduce((total, answer) => total + (answer.suspicious_event_count ?? 0), 0);
      return {
        answered: totals.answered + answered,
        correct: totals.correct + correct,
        flags: totals.flags + flags
      };
    },
    { answered: 0, correct: 0, flags: 0 }
  );

  return (
    <div className="content-grid">
      <section className="panel span-3">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Read-only archive</p>
            <h2>Calendar history</h2>
          </div>
          <CalendarDays />
        </div>
        {history.isLoading ? <p>Loading history...</p> : null}
        {selectedWeek ? (
          <>
            <div className="week-switcher" aria-label="History weeks">
              {weeks.map((week) => (
                <button
                  key={week.id}
                  className={week.id === selectedWeek.id ? "active" : ""}
                  onClick={() => {
                    setSelectedWeekId(week.id);
                    setSelectedDay(1);
                  }}
                >
                  <span>{formatWeekLabel(week.start_at)}</span>
                  <small>{week.status}</small>
                </button>
              ))}
            </div>
            <div className="history-layout">
              <div className="history-calendar">
                {[1, 2, 3, 4, 5, 6, 7].map((day) => {
                  const questions = selectedWeek.questions.filter((question) => question.dayOfWeek === day);
                  const answered = questions.reduce((total, question) => total + question.answers.filter((answer) => answer.submitted_at).length, 0);
                  const correct = questions.reduce((total, question) => total + question.answers.filter((answer) => answer.is_correct).length, 0);
                  const flags = questions.reduce(
                    (total, question) => total + question.answers.reduce((sum, answer) => sum + (answer.suspicious_event_count ?? 0), 0),
                    0
                  );
                  const categoryCount = new Set(questions.map((question) => question.category)).size;
                  return (
                    <button key={day} className={`history-day ${selectedDay === day ? "selected" : ""}`} onClick={() => setSelectedDay(day)}>
                      <span className="history-day-date">{formatHistoryDay(selectedWeek.start_at, day)}</span>
                      <b>{dayName(day)}</b>
                      <small>
                        {questions.length} questions / {categoryCount} categories
                      </small>
                      {selectedWeek.reveal ? (
                        <span className="history-day-metrics">
                          <i>{answered} answered</i>
                          <i>{correct} correct</i>
                          <i>{flags} flags</i>
                        </span>
                      ) : (
                        <span className="history-day-metrics">
                          <i>Hidden</i>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <aside className="history-detail" aria-live="polite">
                <div className="panel-heading">
                  <div>
                    <p className="eyebrow">{formatHistoryDay(selectedWeek.start_at, selectedDay)}</p>
                    <h3>{dayName(selectedDay)}</h3>
                    <p className="fine-print">{selectedWeek.reveal ? "Questions, correct answers, and player answers are visible." : "Question text stays hidden until the week locks."}</p>
                  </div>
                  <Pill icon={selectedWeek.reveal ? <ScrollText size={16} /> : <EyeOff size={16} />} label={selectedWeek.status} />
                </div>
                {selectedWeek.reveal ? (
                  <div className="history-metrics">
                    <span>{selectedTotals.answered} answered</span>
                    <span>{selectedTotals.correct} correct</span>
                    <span>{selectedTotals.flags} flags</span>
                  </div>
                ) : null}
                <div className="history-question-list">
                  {selectedQuestions.map((question) => (
                    <article className="history-question" key={question.id}>
                      <div>
                        <p className="eyebrow">#{question.displayOrder}</p>
                        <h4>{question.category}</h4>
                        <small>
                          {question.difficulty} / {question.answerType === "textInput" ? "typed" : "choice"}
                        </small>
                      </div>
                      {question.prompt ? <p>{question.prompt}</p> : <p className="fine-print">Hidden until Sunday midnight PT.</p>}
                      {question.correctChoice || question.correctTextAnswer ? <p className="fine-print">Correct: {question.correctTextAnswer ?? question.correctChoice}</p> : null}
                      {selectedWeek.reveal ? (
                        <div className="answer-audit-list">
                          {question.answers.map((answer) => (
                            <span key={answer.name}>
                              <b>{answer.name}</b>
                              <em>{answer.displayAnswer ?? "No answer"}</em>
                              <i>{answer.voided_for_cheat ? "voided" : answer.needs_manual_review ? "review" : answer.is_correct ? "correct" : answer.submitted_at ? "miss" : "open"}</i>
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </article>
                  ))}
                </div>
              </aside>
            </div>
          </>
        ) : history.isLoading ? null : (
          <p>No history yet.</p>
        )}
      </section>
    </div>
  );
}

function formatWeekLabel(startAt: string): string {
  return `${formatHistoryDay(startAt, 1)} week`;
}

function formatHistoryDay(weekStartAt: string, dayOfWeek: number): string {
  const date = new Date(new Date(weekStartAt).getTime() + (dayOfWeek - 1) * 24 * 60 * 60 * 1000);
  return date.toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric" });
}

function dayName(dayOfWeek: number): string {
  return ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"][dayOfWeek - 1] ?? "Day";
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("en-US", {
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function AdminPanel() {
  const [provider, setProvider] = useState<"openai" | "anthropic">("openai");
  const [category, setCategory] = useState("Seattle Mariners");
  const [difficulty, setDifficulty] = useState<"easy" | "medium" | "hard">("medium");
  const [answerType, setAnswerType] = useState<"multipleChoice" | "textInput">("multipleChoice");
  const [count, setCount] = useState(10);
  const [textInputPerDay, setTextInputPerDay] = useState(5);
  const [targetWeek, setTargetWeek] = useState<"next" | "current">("next");
  const [apiKeyOverride, setApiKeyOverride] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>(CORE_CATEGORIES.slice(0, 18));
  const queryClient = useQueryClient();
  const overview = useQuery({
    queryKey: ["admin"],
    queryFn: () =>
      api<{
        users: unknown[];
        sessions: Array<{ id: string; user_id: string; name: string; email: string; created_at: string; expires_at: string }>;
        audit: Array<{ id: string; actor_user_id: string; action: string; entity_type: string; entity_id: string; metadata: string; created_at: string; name: string; email: string }>;
        antiCheat: Array<{ id: string; name: string; event_type: string; occurred_at: string; remaining_ms: number }>;
        disputes: AdminDispute[];
      }>("/api/admin/overview")
  });
  const questions = useQuery({
    queryKey: ["admin-questions"],
    queryFn: () =>
      api<{
        total: number;
        active: number;
        byCategory: Array<{ category: string; count: number }>;
        bySource: Array<{ source: string; count: number }>;
        byAnswerType: Array<{ answer_type: "multipleChoice" | "textInput"; count: number }>;
        blind: boolean;
      }>("/api/admin/questions")
  });
  const generation = useQuery({
    queryKey: ["generation-status"],
    queryFn: () =>
      api<{ providers: { openai: { configured: boolean; model: string; reasoningEffort: string }; anthropic: { configured: boolean; model: string } } }>(
        "/api/admin/generation/status"
      )
  });
  const scheduleOptions = useQuery({
    queryKey: ["schedule-options"],
    queryFn: () =>
      api<{
        categories: Array<{ category: string; count: number; hard_count: number; multiple_choice_count: number; text_input_count: number }>;
        weeklyQuestionCount: number;
      }>("/api/admin/schedule/options")
  });
  const recentQuestions = useQuery({
    queryKey: ["admin-recent-questions", category],
    queryFn: () =>
      api<{ questions: RecentGeneratedQuestion[] }>(
        `/api/admin/questions/recent?category=${encodeURIComponent(category)}&limit=12`
      )
  });
  const generate = useMutation({
    mutationFn: () =>
      api<{
        inserted: number;
        attempted: number;
        skipped: number;
        requested: number;
        candidates: number;
        valid: number;
        generated: number;
        provider: string;
        model: string;
        active: boolean;
        answerType: "multipleChoice" | "textInput";
        insertedQuestions: PromptOnlyQuestion[];
      }>("/api/admin/generate/questions", {
        method: "POST",
        body: JSON.stringify({
          provider,
          category,
          difficulty,
          answerType,
          count,
          active: true,
          apiKeyOverride: apiKeyOverride || undefined
        })
      }),
    onSuccess: () => {
      setApiKeyOverride("");
      queryClient.invalidateQueries({ queryKey: ["admin-questions"] });
      queryClient.invalidateQueries({ queryKey: ["schedule-options"] });
      queryClient.invalidateQueries({ queryKey: ["admin-recent-questions", category] });
    }
  });
  const schedule = useMutation({
    mutationFn: () =>
      api<{
        targetWeek: "current" | "next";
        weekId: string;
        weekStartAt: string;
        weekEndAt: string;
        scheduled: number;
        categories: Array<{ category: string; count: number }>;
        hardCount: number;
        multipleChoiceCount: number;
        textInputCount: number;
      }>(
        "/api/admin/weeks/schedule",
        {
        method: "POST",
        body: JSON.stringify({ categories: selectedCategories, force: false, textInputPerDay, targetWeek })
        }
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["dashboard"] })
  });
  const availableCategories =
    scheduleOptions.data?.categories ?? CORE_CATEGORIES.map((category) => ({ category, count: 0, hard_count: 0, multiple_choice_count: 0, text_input_count: 0 }));

  function toggleCategory(next: string) {
    setSelectedCategories((current) => (current.includes(next) ? current.filter((item) => item !== next) : [...current, next]));
  }

  return (
    <div className="content-grid">
      <section className="panel">
        <p className="eyebrow">Players</p>
        <h2>{overview.data?.users.length ?? 0}</h2>
        <p>Active accounts</p>
      </section>
      <section className="panel">
        <p className="eyebrow">Question bank</p>
        <h2>{questions.data?.total ?? 0}</h2>
        <p>{questions.data?.active ?? 0} active. Blind mode keeps prompts, choices, and answers hidden.</p>
        <div className="mini-list">
          {questions.data?.byAnswerType.map((row) => (
            <span key={row.answer_type}>
              {row.answer_type === "textInput" ? "Text input" : "Multiple choice"}: {row.count}
            </span>
          ))}
        </div>
      </section>
      <section className="panel">
        <p className="eyebrow">AI providers</p>
        <div className="provider-list">
          <span>
            OpenAI {generation.data?.providers.openai.configured ? "configured" : "key needed"}
            {generation.data?.providers.openai.model ? (
              <small>
                {generation.data.providers.openai.model} / {generation.data.providers.openai.reasoningEffort} reasoning
              </small>
            ) : null}
          </span>
          <span>
            Claude {generation.data?.providers.anthropic.configured ? "configured" : "key needed"}
            {generation.data?.providers.anthropic.model ? <small>{generation.data.providers.anthropic.model}</small> : null}
          </span>
        </div>
      </section>
      <section className="panel span-2">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Blind generation</p>
            <h2>Build question bank</h2>
          </div>
          <Wand2 />
        </div>
        <div className="form-grid">
          <label>
            Provider
            <select value={provider} onChange={(event) => setProvider(event.target.value as "openai" | "anthropic")}>
              <option value="openai">OpenAI</option>
              <option value="anthropic">Claude</option>
            </select>
          </label>
          <label>
            Category
            <select value={category} onChange={(event) => setCategory(event.target.value)}>
              {CORE_CATEGORIES.map((cat) => (
                <option key={cat}>{cat}</option>
              ))}
            </select>
          </label>
          <label>
            Difficulty
            <select value={difficulty} onChange={(event) => setDifficulty(event.target.value as "easy" | "medium" | "hard")}>
              <option value="easy">easy</option>
              <option value="medium">medium</option>
              <option value="hard">hard</option>
            </select>
          </label>
          <label>
            Answer type
            <select value={answerType} onChange={(event) => setAnswerType(event.target.value as "multipleChoice" | "textInput")}>
              <option value="multipleChoice">multiple choice</option>
              <option value="textInput">text input</option>
            </select>
          </label>
          <label>
            Count
            <input type="number" min={1} max={500} value={count} onChange={(event) => setCount(Number(event.target.value))} />
          </label>
        </div>
        <label>
          One-time API key override
          <input value={apiKeyOverride} onChange={(event) => setApiKeyOverride(event.target.value)} type="password" autoComplete="off" />
        </label>
        <button className="primary" onClick={() => generate.mutate()} disabled={generate.isPending}>
          <Sparkles size={16} />
          Generate hidden questions
        </button>
        {generate.data ? (
          <p className="success">
            Inserted {generate.data.inserted} of {generate.data.requested} requested {generate.data.answerType === "textInput" ? "typed" : "choice"} questions from{" "}
            {generate.data.provider} using {generate.data.model}.
          </p>
        ) : null}
        {generate.data && (generate.data.skipped > 0 || generate.data.generated < generate.data.requested) ? (
          <p className="warning">
            {generate.data.candidates} candidates returned, {generate.data.valid} valid, {generate.data.generated} unique, {generate.data.skipped} duplicate or
            already-stored prompts skipped.
          </p>
        ) : null}
        <MutationError mutation={generate} />
        {generate.data?.insertedQuestions.length ? (
          <div className="prompt-preview saved-preview">
            <div className="panel-heading compact-heading">
              <div>
                <p className="eyebrow">Just saved</p>
                <h3>{generate.data.insertedQuestions.length} prompt-only preview</h3>
              </div>
              <EyeOff size={18} />
            </div>
            <p className="fine-print">These prompts were written to the question bank. Answers and explanations stay hidden.</p>
            <PromptPreviewList questions={generate.data.insertedQuestions} />
          </div>
        ) : null}
        <div className="prompt-preview">
          <div className="panel-heading compact-heading">
            <div>
              <p className="eyebrow">Generated prompt preview</p>
              <h3>{category}</h3>
            </div>
            <EyeOff size={18} />
          </div>
          <p className="fine-print">Prompts are visible here for quality control. Choices, answers, and explanations stay hidden.</p>
          {recentQuestions.isLoading ? <p>Loading recent prompts...</p> : null}
          {recentQuestions.data?.questions.length === 0 ? <p>No generated prompts yet for this category.</p> : null}
          <PromptPreviewList questions={recentQuestions.data?.questions ?? []} />
        </div>
      </section>
      <section className="panel span-3">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Weekly mix</p>
            <h2>Choose categories for a week</h2>
            <p className="fine-print">
              Next week prepares the board after this week locks. Current week changes the live board only if nobody has answered yet.
            </p>
          </div>
          <RefreshCw />
        </div>
        <div className="segmented-control" aria-label="Schedule target">
          <button className={targetWeek === "next" ? "active" : ""} onClick={() => setTargetWeek("next")}>
            Upcoming week
          </button>
          <button className={targetWeek === "current" ? "active" : ""} onClick={() => setTargetWeek("current")}>
            Current week
          </button>
        </div>
        <div className="category-picker">
          {availableCategories.map((row) => {
            const name = row.category;
            const total = row.count;
            const hard = row.hard_count;
            return (
              <button key={name} className={selectedCategories.includes(name) ? "selected" : ""} onClick={() => toggleCategory(name)}>
                <span>{name}</span>
                <small>
                  {total} total / {hard} hard / {row.multiple_choice_count} choice / {row.text_input_count} typed
                </small>
              </button>
            );
          })}
        </div>
        <label>
          Text-input questions per day
          <input type="number" min={0} max={10} value={textInputPerDay} onChange={(event) => setTextInputPerDay(Number(event.target.value))} />
        </label>
        <button className="primary" onClick={() => schedule.mutate()} disabled={schedule.isPending || selectedCategories.length === 0}>
          Schedule {WEEKLY_QUESTION_COUNT} questions for {targetWeek === "next" ? "upcoming week" : "current week"}
        </button>
        {schedule.data ? (
          <p className="success">
            Scheduled {schedule.data.scheduled} questions for the {schedule.data.targetWeek === "next" ? "upcoming" : "current"} week starting{" "}
            {formatDateTime(schedule.data.weekStartAt)} with {schedule.data.hardCount} hard, {schedule.data.multipleChoiceCount} choice,{" "}
            {schedule.data.textInputCount} typed.
          </p>
        ) : null}
        <MutationError mutation={schedule} />
      </section>
      <section className="panel span-2">
        <p className="eyebrow">Bank coverage</p>
        <div className="coverage-list">
          {questions.data?.byCategory.slice(0, 12).map((row) => (
            <span key={row.category}>
              {row.category}
              <b>{row.count}</b>
            </span>
          ))}
        </div>
      </section>
      <section className="panel span-2">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Activity</p>
            <h2>Logins and admin actions</h2>
          </div>
          <Clock3 />
        </div>
        <ActivityFeed overview={overview.data} />
      </section>
      <DisputePortal disputes={overview.data?.disputes ?? []} />
    </div>
  );
}

function DisputePortal({ disputes }: { disputes: AdminDispute[] }) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState<Record<string, string>>({});
  const resolve = useMutation({
    mutationFn: ({ id, action, adminNote }: { id: string; action: "reject" | "credit_answer" | "nullify_answer" | "nullify_question"; adminNote?: string }) =>
      api(`/api/admin/disputes/${id}/resolve`, {
        method: "POST",
        body: JSON.stringify({ action, adminNote: adminNote || undefined })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["history"] });
    }
  });

  return (
    <section className="panel span-3">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">Dispute portal</p>
          <h2>Review flagged questions</h2>
          <p className="fine-print">Credit a player, reject the dispute, nullify one answer, or nullify the question instance for everyone.</p>
        </div>
        <ShieldAlert />
      </div>
      <div className="dispute-list">
        {disputes.map((dispute) => {
          const isOpen = dispute.status === "open";
          const playerAnswer = dispute.answer?.textAnswer ?? dispute.answer?.selectedChoice ?? "No submitted answer";
          const acceptedAnswer = dispute.question.correctTextAnswer ?? dispute.question.correctChoice ?? "No accepted answer";
          const resultLabel = dispute.answer?.voidedByAdmin
            ? "admin voided"
            : dispute.answer?.voidedForCheat
              ? "focus voided"
              : dispute.answer?.needsManualReview
                ? "needs review"
                : dispute.answer?.isCorrect
                  ? "correct"
                  : dispute.answer?.submittedAt
                    ? "miss"
                    : "open";
          return (
            <article className={`dispute-card ${isOpen ? "open" : "resolved"}`} key={dispute.id}>
              <div className="dispute-card-top">
                <div>
                  <p className="eyebrow">
                    {dispute.question.category} / {dispute.question.answerType === "textInput" ? "typed" : "choice"} / {dispute.question.difficulty}
                  </p>
                  <h3>{dispute.playerName}</h3>
                </div>
                <Pill icon={<ShieldAlert size={16} />} label={dispute.status.replaceAll("_", " ")} />
              </div>
              <p className="dispute-question">{dispute.question.prompt}</p>
              <div className="dispute-detail-grid">
                <span>
                  <b>Player answer</b>
                  {playerAnswer}
                </span>
                <span>
                  <b>Accepted answer</b>
                  {acceptedAnswer}
                </span>
                <span>
                  <b>Result</b>
                  {resultLabel}
                </span>
                <span>
                  <b>Flags</b>
                  {dispute.answer?.suspiciousEventCount ?? 0}
                </span>
              </div>
              <p className="fine-print">
                {dispute.reason} {dispute.placement.triviaOffName ? `/${dispute.placement.triviaOffName}` : ""}
              </p>
              <p className="fine-print">{dispute.question.explanation}</p>
              {dispute.adminNote ? <p className="success">Admin note: {dispute.adminNote}</p> : null}
              <textarea
                value={notes[dispute.id] ?? ""}
                onChange={(event) => setNotes((current) => ({ ...current, [dispute.id]: event.target.value }))}
                placeholder="Optional admin note"
                disabled={!isOpen}
              />
              <div className="dispute-actions">
                <button disabled={!isOpen || resolve.isPending} onClick={() => resolve.mutate({ id: dispute.id, action: "reject", adminNote: notes[dispute.id] })}>
                  Reject
                </button>
                <button
                  className="primary"
                  disabled={!isOpen || !dispute.answer || resolve.isPending}
                  onClick={() => resolve.mutate({ id: dispute.id, action: "credit_answer", adminNote: notes[dispute.id] })}
                >
                  Credit player
                </button>
                <button
                  disabled={!isOpen || !dispute.answer || resolve.isPending}
                  onClick={() => resolve.mutate({ id: dispute.id, action: "nullify_answer", adminNote: notes[dispute.id] })}
                >
                  Nullify answer
                </button>
                <button
                  disabled={!isOpen || !dispute.answer || resolve.isPending}
                  onClick={() => resolve.mutate({ id: dispute.id, action: "nullify_question", adminNote: notes[dispute.id] })}
                >
                  Nullify question
                </button>
              </div>
            </article>
          );
        })}
        {!disputes.length ? <p className="fine-print">No disputes yet.</p> : null}
      </div>
      <MutationError mutation={resolve} />
    </section>
  );
}

function ActivityFeed({
  overview
}: {
  overview?:
    | {
        sessions: Array<{ id: string; name: string; email: string; created_at: string; expires_at: string }>;
        audit: Array<{ id: string; action: string; entity_type: string; entity_id: string; metadata: string; created_at: string; name: string; email: string }>;
        antiCheat: Array<{ id: string; name: string; event_type: string; occurred_at: string; remaining_ms: number }>;
      }
    | undefined;
}) {
  const sessions = overview?.sessions.slice(0, 6) ?? [];
  const audit = overview?.audit.slice(0, 8) ?? [];
  const antiCheat = overview?.antiCheat.slice(0, 4) ?? [];
  return (
    <div className="activity-stack">
      <div>
        <h3>Recent logins</h3>
        <div className="activity-list">
          {sessions.map((session) => (
            <span key={session.id}>
              <b>{session.name}</b>
              <em>{formatDateTime(session.created_at)}</em>
              <small>{session.email}</small>
            </span>
          ))}
          {!sessions.length ? <p className="fine-print">No sessions yet.</p> : null}
        </div>
      </div>
      <div>
        <h3>Meaningful actions</h3>
        <div className="activity-list">
          {audit.map((item) => (
            <span key={item.id}>
              <b>{item.name}</b>
              <em>{formatAuditAction(item.action)}</em>
              <small>{formatDateTime(item.created_at)}</small>
            </span>
          ))}
          {!audit.length ? <p className="fine-print">No audit events yet.</p> : null}
        </div>
      </div>
      <div>
        <h3>Recent focus flags</h3>
        <div className="activity-list compact-activity">
          {antiCheat.map((event) => (
            <span key={event.id}>
              <b>{event.name}</b>
              <em>{event.event_type}</em>
              <small>{formatDateTime(event.occurred_at)}</small>
            </span>
          ))}
          {!antiCheat.length ? <p className="fine-print">No focus flags yet.</p> : null}
        </div>
      </div>
    </div>
  );
}

function PromptPreviewList({ questions }: { questions: PromptOnlyQuestion[] }) {
  if (!questions.length) return null;
  return (
    <div className="prompt-preview-list">
      {questions.map((question) => (
        <article key={question.id}>
          <small>
            {question.difficulty} / {question.answerType === "textInput" ? "typed" : "choice"} / {new Date(question.createdAt).toLocaleString()}
          </small>
          <p>{question.prompt}</p>
        </article>
      ))}
    </div>
  );
}

function formatAuditAction(action: string): string {
  return action.replaceAll("_", " ").replaceAll(".", " ");
}

function Leaderboard({ rows, expanded = false }: { rows: LeaderboardRow[]; expanded?: boolean }) {
  return (
    <section className={`leaderboard ${expanded ? "leaderboard-page" : ""}`}>
      <div className="panel-heading">
        <div>
          <p className="eyebrow">Live rankings</p>
          <h2>Leaderboard</h2>
          {expanded ? <p className="fine-print">Ranked by Bomber Bucks, weekly correctness, then speed. Trivia-Off payouts move the same balance.</p> : null}
        </div>
        <Trophy />
      </div>
      {expanded ? (
        <div className="leader-summary">
          <Stat label="Players" value={String(rows.length)} icon={<UserRound />} />
          <Stat label="Answered" value={String(rows.reduce((total, row) => total + row.weeklyAnswered, 0))} icon={<Check />} />
          <Stat label="Flags" value={String(rows.reduce((total, row) => total + row.weeklyFlags, 0))} icon={<ShieldAlert />} />
        </div>
      ) : null}
      {rows.map((row, index) => (
        <div className="leader-row rich" key={row.userId}>
          <span className="rank">{index + 1}</span>
          <span className="avatar" style={{ background: row.avatarColor }}>
            {row.name.slice(0, 1)}
          </span>
          <div className="leader-name">
            <strong>{row.name}</strong>
            <span>{row.weeklyAnswered}/{row.weeklyTotal} answered</span>
            <div className="leader-progress" aria-label={`${row.name} weekly completion`}>
              <i style={{ width: `${row.weeklyTotal ? Math.round((row.weeklyAnswered / row.weeklyTotal) * 100) : 0}%` }} />
            </div>
          </div>
          <div className="leader-metrics">
            <span className="leader-metric">
              <Check size={15} />
              {row.weeklyCorrect} correct
            </span>
            <span className="leader-metric">
              <X size={15} />
              {row.weeklyIncorrect} miss
            </span>
            <span className="leader-metric">
              <Trophy size={15} />
              {row.weeklyAnswered ? `${Math.round((row.weeklyCorrect / row.weeklyAnswered) * 100)}%` : "0%"}
            </span>
            <span className="leader-metric">
              <Clock3 size={15} />
              {row.averageResponseMs ? `${(row.averageResponseMs / 1000).toFixed(1)}s avg` : "no avg"}
            </span>
            <span className="leader-metric">
              <ScrollText size={15} />
              {row.weeklyFlags} flags
            </span>
          </div>
          <b>{formatBucks(row.bomberBucks)} BB</b>
        </div>
      ))}
    </section>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="stat">
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Pill({ label, icon, className = "" }: { label: string; icon?: React.ReactNode; className?: string }) {
  return (
    <span className={`pill ${className}`.trim()}>
      {icon}
      {label}
    </span>
  );
}

function MutationError({ mutation }: { mutation: { error: Error | null } }) {
  return mutation.error ? <p className="error">{mutation.error.message}</p> : null;
}

createRoot(document.getElementById("root")!).render(<App />);
